import { Dropdown, DropdownProps, DropdownChangeEvent } from 'primereact/dropdown';
import { Column } from '../container/Container';
import { TextBodyExtraExtraSmall } from '../text/Texts';
import { cn } from '../ui/cn';
import { useState } from 'react';
import { FormElementProps } from './InputController';
import { MdArrowDropDown } from 'react-icons/md';
import { inputStyle, labelStyle } from './styles';

interface DropdownInputProps extends Omit<DropdownProps, 'onChange' | 'onBlur' | 'appendTo'>, FormElementProps {
	placeholder?: string;
	label?: string;
	labelBgColor?: string;
	containerCls?: string;
	appendTo?: 'default' | 'self'
	scrollable?: boolean;
}

const DropdownInput = ({
	id,
	value,
	onChange,
	onBlur,
	error,

	label,
	placeholder,
	containerCls,
	labelBgColor,
	appendTo = 'self',
	variant = 'filled',
	scrollable = true,
	...dropdownProps
}: DropdownInputProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const needScroll = scrollable && (dropdownProps.options?.length ?? 0) > 4;
	const hasValue = value !== null && value !== undefined && value !== '';
	const dropdownIcon = (
		<MdArrowDropDown
			size={24}
			className={cn(
				'text-onSurfaceVariant transition-transform duration-300',
				isExpanded ? 'rotate-0' : '-rotate-180'
			)}
		/>
	);

	return (
		<Column className={cn('w-full relative', containerCls, dropdownProps.disabled && 'opacity-50')}>
			<Dropdown
				unstyled
				{...dropdownProps}
				appendTo={appendTo === 'default' ? undefined : 'self'}
				id={id}
				value={value}
				onChange={(e: DropdownChangeEvent) => onChange?.(e.value)}
				dropdownIcon={dropdownIcon}
				collapseIcon={dropdownIcon}
				onShow={() => setIsExpanded(true)}
				onHide={() => {
					setIsExpanded(false);
					onBlur?.()
				}}
				variant={undefined}
				pt={{
					root: { className: inputStyle({ variant, isFocus: isExpanded, error: !!error }) },
					trigger: { className: 'absolute top-4 right-3' },
					panel: {
						className: cn(
							'bg-surfaceContainer rounded-b-[4px]',
							'w-full py-2 blue-scroll',
							needScroll ? 'max-h-[240px] pr-1' : '',
							'shadow-[0px_2px_6px_2px_#00000026,0px_1px_2px_0px_#0000004D]',
						)
					},
					wrapper: {
						className: cn('overflow-y-auto', needScroll && 'pr-1'),
						style: { maxHeight: needScroll ? '224px' : '384px' }
					},
					item: { className: 'px-3 py-3.5 focus-visible:outline-none hover:bg-[#D8DDE0] data-[p-highlight=true]:bg-[#D8DDE0]' },
					itemLabel: { className: 'font-poppins text-body-large text-onSurface' },
				}}
			/>

			{/* Placeholder */}
			{(variant === 'filled' && placeholder && !hasValue) && (
				<span
					children={placeholder}
					className={cn(
						'absolute left-4 top-6',
						'transition-opacity duration-300 pointer-events-none',
						'font-poppins text-body-large text-onSurfaceVariant',
						'opacity-0', isExpanded && 'opacity-100'
					)}
				/>
			)}

			{/* Label */}
			{label && (
				<label
					htmlFor={id}
					children={label}
					className={labelStyle({ variant, isFocus: isExpanded, hasValue, labelBgColor, error: !!error })}
				/>
			)}

			{/* Error */}
			{(error && error.message) && (
				<TextBodyExtraExtraSmall children={error.message} className="pl-4 pt-1 text-error" />
			)}
		</Column>
	);
};

export default DropdownInput;
